import { getTableConfig } from 'drizzle-orm/pg-core';
import * as schema from './schema';
import { SYNCED_TABLES } from './schema';

export interface SyncColumn {
  name: string;
  sqlType: string;
  notNull: boolean;
}

export interface SyncTable {
  name: (typeof SYNCED_TABLES)[number];
  columns: SyncColumn[];
  columnNames: ReadonlySet<string>;
  /** Rows are never updated after insert (versions, audit events). */
  insertOnly: boolean;
  hasUpdatedAt: boolean;
  hasDeletedAt: boolean;
}

/** Tables whose rows can only be added. Corrections are new rows. */
const INSERT_ONLY = new Set(['template_versions', 'report_versions', 'audit_events']);

/** Columns the server owns on every row; a client value for them is ignored. */
export const SERVER_COLUMNS = ['rev', 'org_id', 'updated_by'] as const;

/**
 * Settings where the most recent edit wins, on whichever device it was made, as in WhatsApp:
 * a change made with an older revision still goes through when its `updated_at` (the moment it
 * was made on the device) is later than the server's. The device whose edit was older quietly
 * takes the newer one; nobody is asked to settle it.
 */
export const LATEST_EDIT_WINS: ReadonlySet<string> = new Set(['brand_settings', 'signatories']);

/** Columns only the server may set, per table (issued codes). */
export const SERVER_ONLY_VALUES: Record<string, string[]> = {
  reports: ['verify_code'],
  visits: ['track_code'],
};

const byName: Record<string, unknown> = {};
for (const value of Object.values(schema)) {
  try {
    const cfg = getTableConfig(value as never);
    if (cfg?.name) byName[cfg.name] = value;
  } catch {
    // not a table
  }
}

export const SYNC_TABLES: SyncTable[] = SYNCED_TABLES.map((name) => {
  const cfg = getTableConfig(byName[name] as never);
  const columns = cfg.columns.map((c) => ({
    name: c.name,
    sqlType: c.getSQLType(),
    notNull: c.notNull,
  }));
  const columnNames = new Set(columns.map((c) => c.name));
  return {
    name,
    columns,
    columnNames,
    insertOnly: INSERT_ONLY.has(name),
    hasUpdatedAt: columnNames.has('updated_at'),
    hasDeletedAt: columnNames.has('deleted_at'),
  };
});

const TABLES = new Map(SYNC_TABLES.map((t) => [t.name as string, t]));

export function syncTable(name: string): SyncTable | undefined {
  return TABLES.get(name);
}

/** Quote an identifier taken from the registry (never from user input). */
export function q(ident: string): string {
  return `"${ident.replace(/"/g, '""')}"`;
}

/**
 * Upsert one server row into a local database. `jsonb_populate_record` lets Postgres do all
 * the type conversion, so the same statement works for every table.
 */
/**
 * A row counts as news when its revision is higher, or when the server has filled in a value only
 * it may set (the verification code is issued in the same revision the device already holds).
 */
function newerCondition(table: SyncTable): string {
  const t = q(table.name);
  const issued = (SERVER_ONLY_VALUES[table.name] ?? []).map(
    (c) => `${t}.${q(c)} IS DISTINCT FROM EXCLUDED.${q(c)}`,
  );
  return [`${t}.rev < EXCLUDED.rev`, ...issued].join(' OR ');
}

/**
 * Insert or replace a row from the server's JSON. With `onlyNewer`, a row the device already has
 * at the same or a later revision is left alone, so a server echo of our own change writes
 * nothing and does not wake the screens that watch the table.
 */
export function upsertFromJsonSql(table: SyncTable, opts: { onlyNewer?: boolean } = {}): string {
  const cols = table.columns.map((c) => q(c.name)).join(', ');
  const updates = table.columns
    .filter((c) => c.name !== 'id')
    .map((c) => `${q(c.name)} = EXCLUDED.${q(c.name)}`)
    .join(', ');
  return `INSERT INTO ${q(table.name)} (${cols})
    SELECT ${cols} FROM jsonb_populate_record(NULL::${q(table.name)}, $1::jsonb)
    ON CONFLICT (id) DO UPDATE SET ${updates}${opts.onlyNewer ? ` WHERE ${newerCondition(table)}` : ''}`;
}
